import React from "react";

import ky from "ky";

import {
  Box,
  CircularProgress,
  Container,
  IconButton,
} from "@material-ui/core";
import { Autorenew } from "@material-ui/icons";
import { Alert, AlertTitle } from "@material-ui/lab";

import { publicPath } from "@ip-utils";

/**
 * The VersionCheck finds the current js bundle file name, which has a hash in it, to compare that to the server's bundle.
 * Since every deployment generates a new hash, a different name would indicate a new version.
 * If a difference in hashes is found, it means a new version is available and the user should be notified.
 * If the force flag is found, the user will be notified and the application will reload forcefully.
 * Otherwise, a button will be displayed.
 */
export default class VersionCheck extends React.Component {
  constructor(props) {
    super(props);

    const srcSelector = document.querySelector(
      'script[src*="/static/js/main"]'
    );

    const src = srcSelector?.getAttribute("src");

    this.state = {
      interval:
        (parseInt(process.env.REACT_APP_VERSION_CHECK_INTERVAL) || 5) *
        1000 *
        60,
      source: src,
      url: publicPath(),
    };
  }

  componentDidMount() {
    this.wait();
  }

  componentWillUnmount() {
    this.clear();
  }

  wait = () => {
    this.clear();
    this.timeout = setTimeout(this.check, this.state.interval);
  };

  clear = () => {
    clearTimeout(this.timeout);
  };

  check = () => {
    this.clear();

    const { source, url } = this.state;

    ky.get(url)
      .text()
      .then((data) => {
        if (!data.includes(source)) {
          // Grab force flag from server's meta data
          const forceMatch = data.match(
            /<meta name="version_check_force" content="(.*?)"\/>/i
          );
          const force = forceMatch && forceMatch[1];

          this.setState(
            {
              count: 60,
              force,
              refresh: true,
              time: performance.now(),
            },
            () => {
              if (force) {
                this.tick();
              }
            }
          );
        } else {
          this.wait();
        }
      })
      .catch(() => {
        this.wait();
      });
  };

  tick = () => {
    this.clear();

    if (this.state.count > 0) {
      this.timeout = setTimeout(this.tick, 1000);
      this.setState((prevState) => ({
        count: Math.round(60 - (performance.now() - prevState.time) / 1000),
      }));
    } else {
      window.location.reload(true);
    }
  };

  render() {
    const { count, force, refresh } = this.state;

    if (!refresh) {
      return null;
    }

    const action = force ? (
      <Box position="relative">
        <Box
          alignItems="center"
          display="flex"
          fontSize={16}
          height="100%"
          justifyContent="center"
          lineHeight={1}
          position="absolute"
          width="100%"
        >
          {count}
        </Box>
        <Box alignItems="center" display="flex">
          <CircularProgress
            color="inherit"
            value={(count / 60) * 100}
            variant="static"
          />
        </Box>
      </Box>
    ) : (
      <IconButton
        color="inherit"
        size="small"
        onClick={() => window.location.reload(true)}
      >
        <Autorenew color="inherit" />
      </IconButton>
    );

    return (
      <Box marginTop={3} position="fixed" width="100%" zIndex={99999}>
        <Container>
          <Alert action={action} severity="error" variant="filled">
            <AlertTitle>
              Outdated Version{force ? ": Refreshing" : ""}
            </AlertTitle>
            {force ? (
              <div>
                An update is available and the application needs to refresh.
              </div>
            ) : (
              <div>An update is available, please refresh the application.</div>
            )}
          </Alert>
        </Container>
      </Box>
    );
  }
}
